<template>
  <section class="community-page-wrapper">
    <h1 class="mgn-btm-1">Blogs, Videos, and Events</h1>
    <section class="featured-container desk-hor-mob-col">
      <PostItem v-if="featuredPost" :post="featuredPost" :is-main-post="true" class="featured-post" />
      <div class="secondary-featured-wrapper flex-col">
        <h3 class="sec-title">Featured</h3>
        <PostItem
          v-for="(post, index) in secondaryFeatured"
          :key="post.url + index"
          :post="post"
          :is-secondary-featured="true"
        />
      </div>
    </section>
    <template v-if="sections && sections.length">
      <PageSection v-for="(section, index) in sections" :key="'section' + index" :section="section" />
    </template>
  </section>
</template>

<script setup lang="ts">
import { metaGen } from '~/util/metadata';
import { extractComponent, extractItems } from '~/util/contentstack/csHelpers';
import { fetchCommunity } from '~/util/contentstack/community';
import { CTSPost } from '~/types/contentstack';
import { useRootStore } from '~/store';

const { $bus }: any = useNuxtApp();
const rootStore = useRootStore();
const { locale } = useI18n();
const event = useRequestEvent();

const { data: asyncData, error: asyncError } = await useAsyncData(async () => {
  const req = await fetchCommunity({ event, query: { locale: locale?.value } });
  const reqPage = req?.page;
  const sections = req?.sections;
  const mainContent = req?.mainContent;
  const page = {
    ...reqPage,
    sections,
  };
  if (!page || !mainContent) throw createError({ statusCode: 404, message: 'Page not found' });

  return {
    page,
    mainContent,
  };
});

useAsyncErrorHandling({
  error: asyncError.value,
});

useHead({
  ...(metaGen(rootStore?.brandSettings, {
    ...asyncData.value?.page?.seo,
  }) as any),
});

onMounted(() => {
  $bus.emit('pageView', {
    url: document.URL,
  });
});

const sections = computed<{ [index: string]: any }[]>(() => {
  return asyncData.value?.page?.sections || [];
});
const featuredPost = computed<CTSPost>(() => {
  return extractComponent(asyncData.value?.page?.main_featured_postConnection);
});
const secondaryFeatured = computed<CTSPost[]>(() => {
  return extractItems(asyncData.value?.page?.secondary_featured_postsConnection);
});
</script>

<style lang="scss" scoped>
.component-wrapper {
  padding-left: #{local-functions.rem-calc(15)};
  padding-right: #{local-functions.rem-calc(15)};
  width: 100%;
  max-width: calc(local-vars.$xxl-content-max-width - 4rem);
  margin: 0 auto;
}
.community-page-wrapper {
  width: 100%;
  max-width: calc(local-vars.$xxl-content-max-width - 4rem);
  margin: #{local-functions.rem-calc(15)} auto;
  @media (min-width: local-vars.$xxl-content-max-width) and (max-width: 1655px) {
    margin: 0 1rem;
  }
}
img {
  max-width: 100%;
  border-radius: $border-radius-4;
}

.featured-container {
  width: 100%;
  max-width: calc(local-vars.$xxl-content-max-width - 4rem);
  margin: 0 auto;
  padding-left: #{local-functions.rem-calc(15)};
  padding-right: #{local-functions.rem-calc(15)};
  display: flex;
  padding-bottom: 3rem;
  margin-bottom: 3rem;
  border-bottom: 1px solid $color-neutral-cool-200;
}

.mgn-btm-1 {
  width: 100%;
  max-width: calc(local-vars.$xxl-content-max-width - 4rem);
  margin-left: auto;
  margin-right: auto;
  padding-left: #{local-functions.rem-calc(15)};
  padding-right: #{local-functions.rem-calc(15)};
}

.featured-post {
  @include local-mixins.desktop {
    margin-right: 2rem;
  }
}

.secondary-featured-wrapper {
  padding-left: #{local-functions.rem-calc(15)};
  padding-right: #{local-functions.rem-calc(15)};
  .sec-title {
    margin-bottom: 1rem;
  }
  @include local-mixins.tablet_and_mobile {
    padding-top: 1.5rem;
    border-top: 1px solid $color-neutral-cool-200;
    .sec-title {
      margin-bottom: 0.5rem;
    }
  }
}
</style>
